import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { UnAuthenticatedPageLayout } from 'layouts/unAuthenticatedPage.layout';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { useEmptyCallback, useComponentMount } from 'utilities/hooks.util';
import { useSaga } from 'use-reducer-saga';
import reducer, { initialState } from './localStore/reducer';
import sagas from './localStore/sagas';
import {
  getAnonymousEnquiryStart,
  getAnonymousEnquiryAutoRenewPropertiesStart,
  getAnonymousCredentialStart
} from './localStore/actions';
import { Loading } from 'carbon-components-react';
import {
  getDaysFromNow,
  formFullAddress,
  toLocalDateTimeFormat,
  toLocalDateFormat
} from 'utilities/helper.util';
import { ArrowRight16 } from '@carbon/icons-react';
import {
  DetailsPanel,
  EnquiryDetails,
  EnquiryDetailsExpired
} from 'components';
import { SmallerScreen, MapView } from './components';
import {
  selectScreenSize,
  selectIsAuthenticated
} from 'redux/session/selectors';
import { SCREEN_SIZE, APP_URL } from 'types';

interface ParamTypes {
  enquiryId: string;
}

export const AnonymousView: React.FC = () => {
  const screenSize = useSelector(selectScreenSize) || SCREEN_SIZE.MD;
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const bigScreens = [SCREEN_SIZE.MAX, SCREEN_SIZE.XL, SCREEN_SIZE.LG];

  const [localStore, localDispatch] = useSaga(reducer, initialState, sagas);
  const {
    anonymousEnquiry,
    isLoadingData,
    anonymousEnquiryError,
    anonymousEnquiryAutoRenewProperties,
    isLoadingAutoRenewPropertiesData,
    state,
    credentials
  } = localStore;

  const { username, token } = credentials || {};

  const { enquiryId } = useParams<ParamTypes>();
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const vhToken = queryParams.get('vh');
  const plToken = queryParams.get('pl');

  const [isEnquiryDataRotated, setIsEnquiryDataRotated] =
    useState<boolean>(false);
  const [enquiryData, setEnquiryData] = useState<any>('');
  const [showSentinelLoginIframe, setShowSentinelLoginIframe] =
    useState<boolean>(true);
  const [sentinelSuccesfulLogin, setSentinelSuccesfulLogin] =
    useState<boolean>(false);

  useComponentMount(() => {
    if (enquiryId && (vhToken || plToken) && !isAuthenticated) {
      const token = vhToken ? `vh=${vhToken}` : `pl=${plToken}`;
      const params = {
        enquiryId,
        token
      };
      process.env.REACT_APP_SENTINEL_RESPONSE_COLLATION === 'true' &&
        localDispatch(getAnonymousCredentialStart(params));
    }
  });

  const getEnquiryData = useEmptyCallback((params: any) => {
    localDispatch(getAnonymousEnquiryStart(params));
  });

  const getEnquiryAutoRenewData = useEmptyCallback((params: any) => {
    localDispatch(getAnonymousEnquiryAutoRenewPropertiesStart(params));
  });

  useEffect(() => {
    if (enquiryId && (vhToken || plToken) && !isAuthenticated) {
      const token = vhToken ? `vh=${vhToken}` : `pl=${plToken}`;
      const params = {
        enquiryId,
        token
      };
      getEnquiryData(params);
    } else if (isAuthenticated) {
      history.push({
        pathname: APP_URL.ROOT_URL,
        state: {
          enquiryId: String(enquiryId)
        }
      });
    } else {
      history.push({
        pathname: APP_URL.AUTH,
        state: {
          from: `enquiries/${String(enquiryId)}`
        }
      });
    }
  }, [enquiryId, history, vhToken, plToken, getEnquiryData, isAuthenticated]);

  useEffect(() => {
    if (anonymousEnquiry && !isLoadingData) {
      setEnquiryData(anonymousEnquiry);
    }

    if (
      anonymousEnquiry?.auto_renew ||
      anonymousEnquiry?.auto_renew_edited ||
      (!anonymousEnquiry?.internal_create && anonymousEnquiry?.auto_renew_until)
    ) {
      const token = vhToken ? `vh=${vhToken}` : `pl=${plToken}`;
      const params = {
        enquiryId,
        token
      };
      getEnquiryAutoRenewData(params);
    }

    if (
      getDaysFromNow(anonymousEnquiry?.enquiry_at) >=
      Number(process.env.REACT_APP_SENTINEL_DATA_ROTATION_DAYS)
    ) {
      setIsEnquiryDataRotated(true);
    }
  }, [
    anonymousEnquiry,
    isLoadingData,
    getEnquiryAutoRenewData,
    enquiryId,
    plToken,
    vhToken
  ]);

  useEffect(() => {
    if (
      anonymousEnquiryAutoRenewProperties &&
      anonymousEnquiry &&
      !isLoadingAutoRenewPropertiesData
    ) {
      const newData = {
        ...anonymousEnquiry,
        auto_renew_every: anonymousEnquiryAutoRenewProperties.auto_renew_every,
        auto_renew_until: anonymousEnquiryAutoRenewProperties.auto_renew_until,
        auto_renew_date: anonymousEnquiryAutoRenewProperties.auto_renew_date
      };
      setEnquiryData(newData);
    }
  }, [
    anonymousEnquiryAutoRenewProperties,
    isLoadingAutoRenewPropertiesData,
    anonymousEnquiry
  ]);

  useEffect(() => {
    if (state === 'success' && sentinelSuccesfulLogin) {
      setTimeout(() => {
        setShowSentinelLoginIframe(false);
      }, 5000);
    }
  }, [state, sentinelSuccesfulLogin]);

  window.addEventListener(
    'message',
    (event) => {
      const trustedOrigin = process.env.REACT_APP_SENTINEL_WEB_URL;
      if (event.origin !== trustedOrigin) return;

      if (event.data === 'External app logged in successfully') {
        setSentinelSuccesfulLogin(true);
      }
    },
    false
  );

  return (
    <div
      id="AnonymousView"
      data-testid="AnonymousView"
      className="anonymous-view"
    >
      {state === 'success' &&
        showSentinelLoginIframe &&
        !anonymousEnquiryError && (
          <iframe
            title="Sentinel Login"
            src={`${process.env.REACT_APP_SENTINEL_WEB_URL}/external/auth?username=${username}&token=${token}`}
            className="sentinel-login-iframe"
          />
        )}
      <UnAuthenticatedPageLayout enableHeaderGlobalBar={false}>
        {enquiryData && !isLoadingData && (
          <div id="contentContainer" className="content-container">
            {!isEnquiryDataRotated &&
              process.env.REACT_APP_SENTINEL_RESPONSE_COLLATION === 'true' && (
                <EnquiryDetails
                  isListView={false}
                  isAnonymousView={true}
                  anonymousEnquiryDetailsData={enquiryData}
                  vhToken={vhToken || undefined}
                  plToken={plToken || undefined}
                  sentinelSuccesfulLogin={sentinelSuccesfulLogin}
                />
              )}
            {isEnquiryDataRotated &&
              process.env.REACT_APP_SENTINEL_RESPONSE_COLLATION === 'true' && (
                <EnquiryDetailsExpired
                  isAnonymousView={true}
                  anonymousEnquiryDetailsData={enquiryData}
                  vhToken={vhToken || undefined}
                  plToken={plToken || undefined}
                />
              )}
            {process.env.REACT_APP_SENTINEL_RESPONSE_COLLATION !== 'true' && (
              <>
                <div className="left-container">
                  {bigScreens.includes(screenSize) && (
                    <>
                      <div id="titleUserRef" className="upperSection">
                        <div className="user-ref-container">
                          {enquiryData.user_reference}
                        </div>
                      </div>
                      <div className="lowerSection">
                        <MapView
                          shape={enquiryData.shape}
                          status={enquiryData.status}
                          isExpired={enquiryData.is_expired}
                        />
                        <div className="enquiry-information">
                          <div className="info-row">
                            <div className="info-title">Job site</div>
                            &nbsp;
                            <div className="info-value">
                              {formFullAddress(enquiryData.address)}
                            </div>
                          </div>
                          <div className="info-row">
                            <div className="info-title">Enquiry date</div>
                            &nbsp;
                            <div className="info-value">
                              {toLocalDateTimeFormat(enquiryData.enquiry_at)}
                            </div>
                          </div>
                          <div className="info-row">
                            <div className="info-title">Job dates</div>
                            &nbsp;
                            <div className="info-value">
                              {toLocalDateFormat(enquiryData.start_at)}
                              &nbsp;
                              <ArrowRight16 />
                              &nbsp;
                              {toLocalDateFormat(enquiryData.end_at)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                  {!bigScreens.includes(screenSize) && (
                    <SmallerScreen
                      enquiryData={enquiryData}
                      screenSize={screenSize}
                    />
                  )}
                </div>
                <div className="right-container">
                  <DetailsPanel
                    isListView={false}
                    isAnonymousView={true}
                    anonymousEnquiryDetailsData={enquiryData}
                    vhToken={vhToken || undefined}
                    plToken={plToken || undefined}
                  />
                </div>
              </>
            )}
          </div>
        )}
        {!enquiryData && isLoadingData && <Loading />}
        {anonymousEnquiryError && (
          <div className="error-container">{anonymousEnquiryError}</div>
        )}
      </UnAuthenticatedPageLayout>
    </div>
  );
};

export default React.memo(AnonymousView);
